import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getProjects, deleteProject } from '../../services/projectService';

export default function ViewProjectsTab({ refreshTrigger }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProjects();
  }, [refreshTrigger]);

  const loadProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {};
      if (typeFilter) filters.type = typeFilter;
      if (searchQuery) filters.search = searchQuery;

      const data = await getProjects(filters);
      setProjects(data);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to delete project "${projectName}"?`)) {
      try {
        await deleteProject(projectId);
        loadProjects();
      } catch (err) {
        console.error('Error deleting project:', err);
        setError('Failed to delete project');
      }
    }
  };

  const getStatusColor = (project) => {
    if (project.projectType === 'knowledge_base') {
      return 'primary';
    }
    if (project.dataStatus === 'data_retrieved' || project.reminderStatus === 'sent') {
      return 'success';
    }
    if (project.dataStatus === 'sent' || project.reminderStatus === 'not_sent') {
      return 'warning';
    }
    return 'default';
  };

  const getStatusText = (project) => {
    if (project.projectType === 'knowledge_base') {
      return 'Active';
    }
    return project.dataStatus || project.reminderStatus || 'Not sent';
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const getProjectTypeLabel = (type) => {
    const labels = {
      data_capture: 'Data Capture',
      appointment_reminders: 'Appointment Reminders',
      knowledge_base: 'Knowledge Base',
    };
    return labels[type] || type;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        View Projects
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          label="Filter by Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="data_capture">Data Capture</MenuItem>
          <MenuItem value="appointment_reminders">Appointment Reminders</MenuItem>
          <MenuItem value="knowledge_base">Knowledge Base</MenuItem>
        </TextField>

        <TextField
          label="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search projects..."
          size="small"
          sx={{ flexGrow: 1 }}
        />

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadProjects}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Section Name</TableCell>
              <TableCell>Project Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Assistant</TableCell>
              <TableCell>File Name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary">
                    No projects found. Create your first project!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow key={project.projectId} hover>
                  <TableCell>{project.sectionName}</TableCell>
                  <TableCell>{project.projectName}</TableCell>
                  <TableCell>
                    <Chip
                      label={getProjectTypeLabel(project.projectType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{project.assistantName || '-'}</TableCell>
                  <TableCell>{project.originalFileName || '-'}</TableCell>
                  <TableCell>{formatFileSize(project.fileSizeBytes)}</TableCell>
                  <TableCell>{formatDate(project.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(project)}
                      color={getStatusColor(project)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleDelete(project.projectId, project.projectName)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
